<script lang="ts">
  // Re-export so the generic constraint on the script-setup tag below can
  // resolve `MinimalFormApi` (the constraint is evaluated before setup).
  export type { MinimalFormApi } from './useFormSubmitState'
</script>

<script setup lang="ts" generic="TForm extends MinimalFormApi">
  import { type HTMLAttributes, watch } from 'vue'
  import { Button, type ButtonVariants } from '../ui/button'
  import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
  } from '../ui/tooltip'
  import {
    type MinimalFormApi,
    type UseFormSubmitStateOptions,
    type FormSubmitReasons
  } from './useFormSubmitState'
  import { useFormSubmit } from './useFormSubmit'

  /**
   * Submit button bound to a tanstack form. Handles the dirty / valid /
   * submitting / validating states automatically:
   *
   * ```vue
   * <FormSubmit :form="form" label="Save" />
   * ```
   *
   * When the form can't submit, the button becomes visually disabled
   * and a tooltip explains why. Priority order: submitting → validating
   * → pristine → invalid.
   *
   * Drops into either an inline form or a modal's `#primary-action`
   * slot — in the modal case, forward `@error` to the modal's
   * `setErrorMessage` method so exceptions from the form's `onSubmit`
   * surface in the modal's error banner:
   *
   * ```vue
   * <modal ref="modal" ...>
   *   <template #primary-action>
   *     <FormSubmit
   *       :form="form"
   *       label="Save"
   *       @error="(msg) => msg && modal?.setErrorMessage(msg)"
   *     />
   *   </template>
   * </modal>
   * ```
   *
   * The default slot lets you customize the button content (icon +
   * label, IconLoading, etc.) while keeping the bound disabled / click
   * wiring.
   */
  const props = withDefaults(
    defineProps<{
      form: TForm
      /** Button label. Ignored if the default slot is used. */
      label?: string
      variant?: ButtonVariants['variant']
      size?: ButtonVariants['size']
      dataTestid?: string
      class?: HTMLAttributes['class']
      /** Forwarded to `useFormSubmitState`. Defaults to `true`. */
      requireDirty?: UseFormSubmitStateOptions['requireDirty']
      /** Override the disabled-reason copy. See `DEFAULT_FORM_SUBMIT_REASONS`. */
      reasons?: FormSubmitReasons
      /** Hide the disabled-reason tooltip even when the button is disabled. */
      hideDisabledReason?: boolean
    }>(),
    {
      label: 'Save',
      variant: 'default',
      size: 'lg',
      requireDirty: true,
      reasons: undefined,
      hideDisabledReason: false
    }
  )

  const emit = defineEmits<{
    /**
     * Fired whenever the captured exception message changes. `undefined`
     * on clear. Modal consumers typically forward this to the modal's
     * `setErrorMessage` so the failure surfaces in the modal's error
     * banner.
     */
    (e: 'error', message: string | undefined): void
  }>()

  const { disabled, isSubmitting, disabledReason, submit, errorMessage } =
    useFormSubmit(props.form, {
      requireDirty: props.requireDirty,
      reasons: props.reasons
    })

  watch(errorMessage, (msg) => emit('error', msg))

  function handleClick() {
    submit()
  }
</script>

<template>
  <TooltipProvider>
    <Tooltip>
      <!--
        Tooltips on disabled buttons need a wrapper. Native disabled
        buttons swallow pointer events, so the tooltip would never see a
        hover. We genuinely disable the button (so click is blocked and
        screen readers announce it correctly) but put `pointer-events:
        none` on it so the hover bubbles up to the wrapper span instead.
        The span is the TooltipTrigger and catches the hover.
      -->
      <TooltipTrigger as-child>
        <span
          :class="['inline-block', disabled && 'cursor-not-allowed']"
          :tabindex="disabled ? 0 : -1"
        >
          <Button
            type="submit"
            :variant="variant"
            :size="size"
            :class="[props.class, disabled && 'pointer-events-none opacity-50']"
            :data-testid="dataTestid"
            :disabled="disabled"
            :aria-busy="isSubmitting || undefined"
            @click="handleClick"
          >
            <slot>{{ label ?? 'Save Changes' }}</slot>
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent v-if="disabled && disabledReason && !hideDisabledReason">
        {{ disabledReason }}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
